import { ReactNode } from "react";
import Link from "next/link";
import { FaWhatsapp } from "react-icons/fa";
import { cn } from "@/lib/utils";

type ButtonVariant = "primary" | "secondary" | "ghost" | "whatsapp" | "gold";
export type ButtonSize = "sm" | "md" | "lg";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-brand-500 text-white hover:bg-brand-600",
  secondary: "border border-line bg-white text-ink hover:bg-surface",
  ghost: "bg-transparent text-ink hover:bg-surface",
  whatsapp: "bg-whatsapp text-white hover:bg-[#20BD5A]",
  gold: "bg-gold-500 text-ink hover:bg-gold-600 hover:text-white",
};

const sizes: Record<ButtonSize, string> = {
  sm: "min-h-9 px-4 py-2",
  md: "min-h-11 px-5 py-2.5",
  lg: "min-h-12 px-6 py-3",
};

export type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  href?: string;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  fullWidth?: boolean;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  target?: "_blank" | "_self";
  rel?: string;
};

export function Button({
  variant = "primary",
  size = "md",
  href,
  onClick,
  type = "button",
  fullWidth,
  children,
  className,
  disabled,
  target,
  rel,
}: ButtonProps) {
  const classes = cn(
    "inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    variants[variant],
    sizes[size],
    fullWidth && "w-full",
    className
  );
  const content = (
    <>
      {variant === "whatsapp" && <FaWhatsapp aria-hidden="true" className="size-4" />}
      {children}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        className={classes}
        onClick={onClick}
        target={target}
        rel={rel}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled}
    >
      {content}
    </button>
  );
}
